export type WorkflowStep = {
  number: number;
  title: string;
  body: string;
};

export type ParsedWorkflow = {
  intro: string;
  steps: WorkflowStep[];
  outro: string;
};

const MIN_STEPS = 2;
const FENCE_RE = /^\s*```/;
const NUMBERED_HEADING_RE = /^(#{1,6})\s+(?:\*\*)?(\d+)(?:\)|\.(?!\d))(?:\*\*)?\s+(.+?)\s*$/;
const NUMBERED_PAREN_RE = /^(?:\*\*)?(\d+)\)(?:\*\*)?\s+(.+?)\s*$/;
const ANY_HEADING_RE = /^(#{1,6})\s+\S/;
const HR_RE = /^-{3,}\s*$/;

type NumberedMatch = {
  lineIndex: number;
  number: number;
  title: string;
  headingLevel: number | null;
};

function cleanTitle(title: string): string {
  return title
    .replace(/\s+#+\s*$/, '')
    .replace(/^\*\*/, '')
    .replace(/\*\*$/, '')
    .trim();
}

function trimSection(text: string): string {
  const lines = text.split('\n');

  while (lines.length && (lines[0].trim() === '' || HR_RE.test(lines[0].trim()))) {
    lines.shift();
  }
  while (
    lines.length &&
    (lines[lines.length - 1].trim() === '' || HR_RE.test(lines[lines.length - 1].trim()))
  ) {
    lines.pop();
  }

  return lines.join('\n').trim();
}

function lineInFence(lines: string[]): boolean[] {
  const result = Array(lines.length).fill(false);
  let fence = false;

  for (let i = 0; i < lines.length; i += 1) {
    if (FENCE_RE.test(lines[i])) {
      result[i] = true;
      fence = !fence;
      continue;
    }
    result[i] = fence;
  }

  return result;
}

function consecutiveNumberedRun(matches: NumberedMatch[]): NumberedMatch[] | null {
  const startZero = matches.findIndex(match => match.number === 0);
  const startOne = matches.findIndex(match => match.number === 1);
  const start = startZero !== -1 ? startZero : startOne;
  if (start === -1) return null;

  const run = [matches[start]];
  let expected = matches[start].number + 1;

  for (let i = start + 1; i < matches.length; i += 1) {
    if (matches[i].number !== expected) break;
    run.push(matches[i]);
    expected += 1;
  }

  return run.length >= MIN_STEPS ? run : null;
}

function collectHeadingMatches(lines: string[], inFence: boolean[]): NumberedMatch[] {
  const matches: NumberedMatch[] = [];

  for (let i = 0; i < lines.length; i += 1) {
    if (inFence[i]) continue;
    const match = lines[i].match(NUMBERED_HEADING_RE);
    if (!match) continue;

    matches.push({
      lineIndex: i,
      number: Number(match[2]),
      title: cleanTitle(match[3]),
      headingLevel: match[1].length,
    });
  }

  return matches;
}

function collectParenMatches(lines: string[], inFence: boolean[]): NumberedMatch[] {
  const matches: NumberedMatch[] = [];

  for (let i = 0; i < lines.length; i += 1) {
    if (inFence[i]) continue;
    if (/^\s/.test(lines[i])) continue;

    const match = lines[i].match(NUMBERED_PAREN_RE);
    if (!match) continue;

    matches.push({
      lineIndex: i,
      number: Number(match[1]),
      title: cleanTitle(match[2]),
      headingLevel: null,
    });
  }

  return matches;
}

function findOutroStart(
  lines: string[],
  inFence: boolean[],
  fromLine: number,
  headingLevel: number | null,
): number {
  for (let i = fromLine; i < lines.length; i += 1) {
    if (inFence[i]) continue;

    const heading = lines[i].match(ANY_HEADING_RE);
    if (!heading) continue;

    const level = heading[1].length;
    const isNumberedStep = NUMBERED_HEADING_RE.test(lines[i]);
    const cutsSection = headingLevel === null ? !isNumberedStep : level <= headingLevel && !isNumberedStep;

    if (cutsSection) return i;
  }

  return lines.length;
}

function buildWorkflow(lines: string[], inFence: boolean[], run: NumberedMatch[]): ParsedWorkflow | null {
  const headingLevel = run[0]?.headingLevel ?? null;
  const last = run[run.length - 1];
  const outroStart = findOutroStart(lines, inFence, last.lineIndex + 1, headingLevel);

  const steps: WorkflowStep[] = run.map((step, index) => {
    const next = run[index + 1];
    const bodyEnd = next ? next.lineIndex : outroStart;
    const body = trimSection(lines.slice(step.lineIndex + 1, bodyEnd).join('\n'));

    return { number: step.number, title: step.title, body };
  });

  if (headingLevel === null && steps.every(step => !step.body)) {
    return null;
  }

  return {
    intro: trimSection(lines.slice(0, run[0].lineIndex).join('\n')),
    steps,
    outro: trimSection(lines.slice(outroStart).join('\n')),
  };
}

export function parseWorkflowSteps(markdown: string): ParsedWorkflow | null {
  const lines = markdown.replace(/\r\n/g, '\n').split('\n');
  const inFence = lineInFence(lines);

  const headingRun = consecutiveNumberedRun(collectHeadingMatches(lines, inFence));
  if (headingRun) return buildWorkflow(lines, inFence, headingRun);

  const parenRun = consecutiveNumberedRun(collectParenMatches(lines, inFence));
  if (parenRun) return buildWorkflow(lines, inFence, parenRun);

  return null;
}
